import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTelegram } from '../context/TelegramContext'
import Header from '../components/Header'
import './ProductImages.css'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

const ProductImages = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAdmin, showAlert } = useTelegram()
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [product, setProduct] = useState(null)
  const [images, setImages] = useState([])
  const [dragActive, setDragActive] = useState(false)

  useEffect(() => {
    if (isAdmin !== true) {
      showAlert?.('Доступ запрещён')
      navigate('/')
      return
    }
    loadProduct()
  }, [id, isAdmin])

  const loadProduct = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_URL}/products/${id}`)
      const data = await response.json()
      
      setProduct(data)

      // Загружаем изображения
      let productImages = []
      if (data.image_urls) {
        try {
          productImages = JSON.parse(data.image_urls)
          console.log('📷 Изображения из БД:', productImages)
        } catch {
          productImages = []
        }
      } else if (data.image_url) {
        productImages = [data.image_url]
        console.log('📷 Одно изображение из БД:', data.image_url)
      }
      setImages(productImages)
    } catch (err) {
      console.error('Ошибка загрузки товара:', err)
      showAlert?.('Не удалось загрузить товар')
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return

    try {
      setUploading(true)
      const formData = new FormData()
      
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i])
      }

      console.log(`📤 Загрузка ${files.length} файлов для товара ID ${id}`)

      const response = await fetch(`${API_URL}/products/${id}/upload-images`, {
        method: 'POST',
        body: formData
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setImages(data.all_images || [])
        showAlert?.(`Загружено ${data.uploaded_files?.length || 0} изображений`)
        console.log('✅ Загрузка успешна:', data)
      } else {
        console.error('❌ Ошибка ответа:', data)
        showAlert?.('Ошибка загрузки изображений')
      }
    } catch (err) {
      console.error('❌ Ошибка загрузки изображений:', err)
      showAlert?.('Ошибка загрузки изображений')
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteImage = async (imagePath) => {
    if (!confirm('Удалить это изображение?')) return

    try {
      const formData = new FormData()
      formData.append('image_path', imagePath)

      const response = await fetch(`${API_URL}/products/${id}/images`, {
        method: 'DELETE',
        body: formData
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setImages(data.remaining_images || [])
        showAlert?.('Изображение удалено')
      } else {
        showAlert?.('Ошибка удаления изображения')
      }
    } catch (err) {
      console.error('Ошибка удаления изображения:', err)
      showAlert?.('Ошибка удаления изображения')
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageUpload(e.dataTransfer.files)
    }
  }

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder.jpg'
    
    // Если путь начинается с http/https - используем как есть
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath
    }
    
    // Убираем лишние слэши и формируем правильный URL
    const cleanPath = imagePath.replace(/^\/+/, '')
    
    // В production используем текущий origin
    if (window.location.hostname !== 'localhost') {
      return `${window.location.origin}/${cleanPath}`
    }
    
    // В development используем API_URL без /api
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
    const baseUrl = apiUrl.replace('/api', '')
    return `${baseUrl}/${cleanPath}`
  }

  if (loading) {
    return (
      <div className="product-images-page">
        <Header title="Изображения товара" showBack={true} />
        <div className="loading">Загрузка...</div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="product-images-page">
        <Header title="Изображения товара" showBack={true} />
        <div className="error">Товар не найден</div>
      </div>
    )
  }

  return (
    <div className="product-images-page">
      <Header title={`Изображения: ${product.name}`} showBack={true} />
      
      <div className="product-images-content">
        <div className="product-info-card">
          <h3>{product.name}</h3>
          <p className="product-price">{product.price} ₽</p>
          <p className="images-count">Изображений: {images.length}</p>
        </div>

        <div 
          className={`image-upload-zone ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="image-upload-input"
            multiple
            accept="image/*"
            onChange={(e) => handleImageUpload(e.target.files)}
            style={{ display: 'none' }}
          />
          <label htmlFor="image-upload-input" className="upload-label">
            <div className="upload-icon">📸</div>
            <div className="upload-text">
              {uploading ? 'Загрузка...' : 'Нажмите или перетащите изображения'}
            </div>
            <div className="upload-hint">Поддерживаются: JPG, PNG, WEBP, GIF</div>
          </label>
        </div>

        {images.length > 0 && (
          <div className="images-grid">
            {images.map((img, index) => (
              <div key={index} className="image-item">
                <img src={getImageUrl(img)} alt={`${product.name} ${index + 1}`} />
                <div className="image-overlay">
                  <button
                    type="button"
                    className="delete-image-btn"
                    onClick={() => handleDeleteImage(img)}
                    title="Удалить изображение"
                  >
                    🗑️ Удалить
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {images.length === 0 && (
          <div className="no-images-message">
            <p>📸 Изображений пока нет</p>
            <p>Загрузите первое изображение для этого товара</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductImages

